import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Trophy, TrendingUp, TrendingDown, Minus, ChevronLeft, FileDown, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * PlayerSpeedIQHistory — `/portal/speediq/history` · the career timeline.
 *
 * Renders the last 12 SpeedIQ sessions' Mechanics / Performance / Cause /
 * Overall scores in a single ROI chart, plus per-layer delta-vs-personal-best
 * chips. Designed so athletes can DM a single visual to recruiters that
 * shows the *trend*, not just a snapshot.
 */
const LAYER_COLORS = {
  mechanics: "#3b82f6",
  performance: "#f59e0b",
  cause: "#a855f7",
  overall: "#D90429",
};

export default function PlayerSpeedIQHistory() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/speediq/me/history?limit=12");
        setData(data);
      } catch (e) {
        setData({ series: [], delta_vs_pb: {}, personal_bests: {}, count: 0 });
      }
    })();
  }, []);

  if (data === null) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-10 text-white/60 text-sm">Loading SpeedIQ history…</div>
    );
  }
  const series = (data.series || []).map((s, i) => ({
    ...s,
    label: s.captured_at ? new Date(s.captured_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : `S${i + 1}`,
  }));
  const latest = series[series.length - 1];
  const empty = series.length === 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6 pb-32" data-testid="player-speediq-history-page">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/portal" className="text-[11px] uppercase tracking-wider text-white/55 hover:text-white inline-flex items-center gap-1" data-testid="back-to-portal">
          <ChevronLeft className="h-3.5 w-3.5" /> Back to portal
        </Link>
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/45">
          SpeedIQ · career timeline
        </div>
      </div>

      {/* Hero — latest score + delta-vs-PB row */}
      <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-5" data-testid="speediq-history-hero">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--brand-secondary)" }}>
              <Trophy className="h-3 w-3" /> Career SpeedIQ timeline
            </div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white mt-2">
              {data.count > 0 ? <>{data.count} scored session{data.count === 1 ? "" : "s"}</> : "No scored sessions yet"}
            </h1>
            <p className="text-[12px] text-white/55 mt-1">Pulls the last 12 SpeedIQ sessions. DM the shared image to recruiters.</p>
          </div>
          {!empty && latest && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-white/45">Latest overall</div>
              <div className="font-display font-black text-5xl tracking-tighter mt-1" style={{ color: LAYER_COLORS.overall }} data-testid="latest-overall-score">
                {latest.overall != null ? Math.round(latest.overall) : "—"}
              </div>
            </div>
          )}
        </div>

        {/* Delta vs PB row */}
        {!empty && data.delta_vs_pb && Object.keys(data.delta_vs_pb).length > 0 && (
          <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="delta-pb-row">
            {["mechanics", "performance", "cause", "overall"].map((k) => {
              const d = data.delta_vs_pb[k];
              if (d == null) return null;
              const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
              const color = d > 0 ? "#10b981" : d < 0 ? "#f87171" : "#94a3b8";
              return (
                <div key={k} className="rounded-md border border-white/10 bg-black/30 px-3 py-2" data-testid={`delta-${k}`}>
                  <div className="text-[10px] uppercase tracking-wider text-white/45">{k}</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="font-mono-num font-bold text-base" style={{ color }}>
                      {d > 0 ? "+" : ""}{d}
                    </span>
                    <Icon className="h-3.5 w-3.5" style={{ color }} />
                  </div>
                  <div className="text-[10px] text-white/40">PB {data.personal_bests[k] != null ? Math.round(data.personal_bests[k]) : "—"}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Chart */}
      {empty ? (
        <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-10 text-center" data-testid="speediq-history-empty">
          <div className="text-white/60 text-sm">No SpeedIQ sessions scored yet. Run a session and your career timeline will appear here.</div>
          <Link to="/speediq" className="inline-block mt-4">
            <Button className="text-white red-glow" style={{ backgroundColor: "var(--brand-secondary)" }}>
              Start a SpeedIQ session
            </Button>
          </Link>
        </Card>
      ) : (
        <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-4" data-testid="speediq-history-chart-card">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mb-2">3-layer score timeline</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 8, right: 10, left: -10, bottom: 6 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" fontSize={11} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} domain={[0, 100]} />
                <Tooltip contentStyle={{ backgroundColor: "#0A1424", border: "1px solid rgba(255,255,255,0.1)" }}
                         labelStyle={{ color: "#fff", fontWeight: 700 }} itemStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }} />
                {["overall", "mechanics", "performance", "cause"].map((k) => (
                  <Line key={k} type="monotone" dataKey={k} stroke={LAYER_COLORS[k]} strokeWidth={k === "overall" ? 3 : 2}
                        dot={{ r: 3, fill: LAYER_COLORS[k] }} activeDot={{ r: 5 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Session table */}
      {!empty && (
        <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] overflow-hidden" data-testid="speediq-history-table">
          <div className="px-4 py-3 border-b border-[var(--ink-border)]">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">Session log · most recent at top</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-white/45">
                <tr>
                  <th className="text-left px-3 py-2.5">Date</th>
                  <th className="text-left px-3 py-2.5">Sport · Test</th>
                  <th className="text-right px-3 py-2.5">Mech</th>
                  <th className="text-right px-3 py-2.5">Perf</th>
                  <th className="text-right px-3 py-2.5">Cause</th>
                  <th className="text-right px-3 py-2.5">Overall</th>
                  <th className="text-right px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {[...series].reverse().map((s) => (
                  <tr key={s.session_id} className="border-t border-white/5" data-testid={`history-row-${s.session_id}`}>
                    <td className="px-3 py-2.5 text-white/80">{s.label}</td>
                    <td className="px-3 py-2.5 text-white/65 text-[12px]">{s.sport_mode} · {s.test_type}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num text-white/85">{s.mechanics != null ? Math.round(s.mechanics) : "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num text-white/85">{s.performance != null ? Math.round(s.performance) : "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num text-white/85">{s.cause != null ? Math.round(s.cause) : "—"}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num font-bold" style={{ color: LAYER_COLORS.overall }}>{s.overall != null ? Math.round(s.overall) : "—"}</td>
                    <td className="px-3 py-2.5 text-right">
                      <SessionActions sessionId={s.session_id} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}


function SessionActions({ sessionId }) {
  async function downloadPdf() {
    try {
      const res = await api.get(`/speediq/sessions/${sessionId}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `speediq-${sessionId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Athlete report downloaded");
    } catch { toast.error("PDF download failed"); }
  }
  async function shareLink() {
    try {
      const { data } = await api.post(`/speediq/sessions/${sessionId}/share`);
      await navigator.clipboard?.writeText(`${window.location.origin}${data.share_path}`);
      toast.success("Public share link copied");
    } catch { toast.error("Could not mint share link"); }
  }
  return (
    <div className="inline-flex items-center gap-1 justify-end">
      <button onClick={downloadPdf} title="Download PDF" className="p-1.5 rounded hover:bg-white/10" data-testid={`history-pdf-${sessionId}`}>
        <FileDown className="h-3.5 w-3.5 text-white/65" />
      </button>
      <button onClick={shareLink} title="Share link" className="p-1.5 rounded hover:bg-white/10" data-testid={`history-share-${sessionId}`}>
        <Share2 className="h-3.5 w-3.5 text-white/65" />
      </button>
    </div>
  );
}
